/**
 * Client-side cache for slow-changing lookup data
 * Supports both memory cache and localStorage with TTL
 */

const CACHE_PREFIX = 'reports_cache_';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

// In-memory cache (fast access, lost on refresh)
const memoryCache = new Map();

/**
 * Get tenant-scoped cache key
 */
const getCacheKey = (key, tenantId) => `${CACHE_PREFIX}${tenantId}_${key}`;

/**
 * Check if cached data is still valid
 */
const isValid = (cached) => {
  if (!cached || !cached.timestamp) return false;
  return Date.now() - cached.timestamp < (cached.ttl || DEFAULT_TTL_MS);
};

/**
 * Get data from cache (memory first, then localStorage)
 */
export const getFromCache = (key, tenantId) => {
  const cacheKey = getCacheKey(key, tenantId);
  
  // Check memory cache first (fastest)
  const memCached = memoryCache.get(cacheKey);
  if (memCached && isValid(memCached)) {
    return memCached.data;
  }
  
  // Check localStorage
  try {
    const stored = localStorage.getItem(cacheKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (isValid(parsed)) {
        // Restore to memory cache
        memoryCache.set(cacheKey, parsed);
        return parsed.data;
      } else {
        // Expired - clean up
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (e) {
    console.warn('Cache read error:', e);
  }
  
  return null;
};

/**
 * Save data to cache (both memory and localStorage)
 */
export const saveToCache = (key, tenantId, data, ttlMs = DEFAULT_TTL_MS) => {
  const cacheKey = getCacheKey(key, tenantId);
  const cached = {
    data,
    timestamp: Date.now(),
    ttl: ttlMs
  };
  
  // Save to memory cache
  memoryCache.set(cacheKey, cached);
  
  // Save to localStorage
  try {
    localStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch (e) {
    console.warn('Cache write error:', e);
  }
};

/**
 * Clear specific cache key
 */
export const clearCache = (key, tenantId) => {
  const cacheKey = getCacheKey(key, tenantId);
  memoryCache.delete(cacheKey);
  try {
    localStorage.removeItem(cacheKey);
  } catch (e) {
    // Ignore
  }
};

/**
 * Clear all reports cache for tenant
 */
export const clearAllCache = (tenantId) => {
  // Clear memory cache
  for (const key of memoryCache.keys()) {
    if (key.includes(`${CACHE_PREFIX}${tenantId}_`)) {
      memoryCache.delete(key);
    }
  }
  
  // Clear localStorage
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes(`${CACHE_PREFIX}${tenantId}_`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    // Ignore
  }
};

/**
 * Fetch with caching - returns cached data if valid, otherwise fetches and caches
 */
export const fetchWithCache = async (key, tenantId, fetchFn, ttlMs = DEFAULT_TTL_MS) => {
  // Check cache first
  const cached = getFromCache(key, tenantId);
  if (cached !== null) {
    return cached;
  }
  
  // Fetch fresh data
  const response = await fetchFn();
  const data = response.data || response;
  
  // Cache the result
  saveToCache(key, tenantId, data, ttlMs);
  
  return data;
};

// Track in-flight requests to prevent duplicate fetches
const inFlightRequests = new Map();

/**
 * Fetch with deduplication - prevents duplicate requests for the same resource
 */
export const fetchWithDedup = async (key, tenantId, fetchFn, ttlMs = DEFAULT_TTL_MS) => {
  const cacheKey = getCacheKey(key, tenantId);
  
  // Check cache first
  const cached = getFromCache(key, tenantId);
  if (cached !== null) {
    return cached;
  }
  
  // Check if request is already in flight
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }
  
  // Create and track the request
  const requestPromise = (async () => {
    try {
      const response = await fetchFn();
      const data = response.data || response;
      saveToCache(key, tenantId, data, ttlMs);
      return data;
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();
  
  inFlightRequests.set(cacheKey, requestPromise);
  return requestPromise;
};
